import { useEffect, useRef, useState } from 'react';
import { Box, Drawer, FormHelperText, IconButton, InputAdornment } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { SendRounded, StopCircle } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import Tooltip from '@mui/material/Tooltip';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import { SenderEnum } from 'pages/chatbox/components/chatBoxWindow';
import { useNotification } from 'hook/useNotification';
import ChatBotRequestDetails from 'pages/chatbox/components/chatBotRequestDetails';
import { generateUUID } from 'utils/cryptoUtils';

/**
 * ChatBoxInputArea component
 *
 * This component represents the input area for sending messages in the chat interface.
 * It includes an input field for the message, an option to attach a file, and a button to send or cancel a message.
 *
 * @param {Function} setMessages - Function to update the messages list.
 * @param {Object} selectedAssistant - The currently selected assistant.
 * @param {string} selectedAssistant.code - The code of the selected assistant.
 * @param {Function} onMessageSend - Function called when a message is sent.
 * @param {boolean} messageLoading - Boolean flag indicating whether a message is being sent.
 * @param {boolean} isEmbedded - Boolean flag indicating if the chatbox is embedded.
 * @param {Function} onCancel - Function to cancel the request
 * @param {boolean} dragInProgress - True if a file is being dragged anywhere on the page (passed from parent).
 */
export default function ChatBoxInputArea({
  setMessages,
  selectedAssistant,
  onMessageSend,
  messageLoading,
  isEmbedded = false,
  onCancel,
  dragInProgress
}) {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  const restrictions = selectedAssistant?.restrictions ?? {};

  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [request, setRequest] = useState({ conversationId: generateUUID() });
  const [requestDetailsDrawerOpen, setRequestDetailsDrawerOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const maxFiles = restrictions?.maxFilesCount ?? 0;
  const fileCountText =
    maxFiles > 1 && files.length > 0
      ? `Pièce${files.length > 1 ? 's' : ''} jointe${files.length > 1 ? 's' : ''} : ${files.length} / ${maxFiles}`
      : '';
  const [invalidFilesMessage, setInvalidFilesMessage] = useState('');

  const messageInputRef = useRef(null);

  useEffect(() => {
    messageInputRef.current?.focus();
  }, [files]);

  const processFiles = (fileList) => {
    const maxCount = restrictions?.maxFilesCount ?? 1;
    const maxTotalSize = (restrictions?.maxFilesSize ?? 0) * 1024; // Ko -> octets
    const allowedExts = restrictions?.allowedExtensions ?? [];

    if (!fileList || fileList.length === 0) return;

    const filesArray = Array.from(fileList);
    const validFiles = [];
    const invalidFiles = [];

    filesArray.forEach((file) => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (allowedExts.length === 0 || allowedExts.map((e) => e.toLowerCase()).includes(ext)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    let errorMsg = ''; // on détermine le message une seule fois

    setFiles((prev) => {
      const combined = [...prev, ...validFiles];
      const limitedByCount =
        maxCount > 0 && combined.length > maxCount ? combined.slice(0, maxCount) : combined;

      if (maxTotalSize > 0) {
        const totalSize = limitedByCount.reduce((sum, f) => sum + f.size, 0);
        if (totalSize > maxTotalSize) {
          let runningSize = 0;
          const sizeFiltered = [];
          for (const f of limitedByCount) {
            if (runningSize + f.size <= maxTotalSize) {
              sizeFiltered.push(f);
              runningSize += f.size;
            } else {
              break;
            }
          }

          errorMsg = msg(
            { id: 'page.chatbox.dropzone.too.large' },
            { maxSize: (maxTotalSize / 1024 / 1024).toFixed(0) + ' Mo' }
          );

          return sizeFiltered;
        }
      }

      return limitedByCount;
    });

    if (invalidFiles.length > 0) {
      errorMsg = msg(
        { id: 'page.chatbox.dropzone.invalid.files' },
        {
          forbiddenFiles: invalidFiles.join(', '),
          allowedExtensions: allowedExts.join(', ')
        }
      );
    }

    setInvalidFilesMessage(errorMsg);
  };

  const handleFileChange = (event) => {
    processFiles(event.target.files);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
    setError('');
  };

  const handleRequestDetailsToggle = (open) => () => {
    setRequestDetailsDrawerOpen(open);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const finalMessage =
      message.trim() ||
      (files.length > 0
        ? files.length > 1
          ? msg({ id: 'page.chatbox.send.default.message.files' })
          : msg({ id: 'page.chatbox.send.default.message.file' })
        : '');

    if (!finalMessage) {
      addNotification({
        type: 'warning',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.chatbox.error.message.required' })
      });
      return;
    }

    if (!selectedAssistant) {
      addNotification({
        type: 'warning',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.chatbox.error.assistant.required' })
      });
      return;
    }

    setMessages((prevMessages) => [
      ...prevMessages,
      { id: generateUUID(), sender: SenderEnum.USER, text: finalMessage }
    ]);

    const finalRequest = {
      ...request,
      query: finalMessage,
      assistantCode: selectedAssistant.code
    };

    onMessageSend(files, finalRequest);

    clearInputs();
  };

  const toggleDrawer = (open) => () => {
    setRequestDetailsDrawerOpen(open);
  };

  function buildRequest(requestForm) {
    const { conversationId, promptCode, parameters } = requestForm;
    const newRequest = {
      conversationId,
      promptCode,
      parameters: parameters ? JSON.parse(parameters) : {}
    };
    if (requestForm.ragActivated) {
      const { ragActivated, ragFilter, ragRelationFilter, ragRelationType } = requestForm;
      newRequest.ragRequest = {
        activated: ragActivated,
        filter: ragFilter,
        relationFilter: { filter: ragRelationFilter, relationType: ragRelationType }
      };
    }
    return newRequest;
  }

  const handleRequestDetailsFormSubmit = (data) => {
    setRequest(buildRequest(data));
    setRequestDetailsDrawerOpen(false);
  };

  // --- Drag & Drop Handlers ---
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    processFiles(event.dataTransfer.files);

    setTimeout(() => {
      messageInputRef.current?.focus();
    }, 0);
  };

  const isDropZoneVisible = maxFiles > 0 && (dragInProgress || isDragOver);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '10px',
          width: '100%',
          maxWidth: '1000px',
          borderRadius: '10px',
          transition: 'border 0.2s ease-in-out'
        }}
      >
        {files.length > 0 && (
          <Box sx={{ marginBottom: 1, display: 'flex', flexDirection: 'row', gap: 0.5 }}>
            {files.map((fileItem, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '10px',
                  padding: 0.5,
                  backgroundColor: '#e5e5ea',
                  width: 'fit-content'
                }}
              >
                <AttachFileIcon sx={{ marginRight: 1 }} />
                <MDTypography variant="body2">{fileItem.name}</MDTypography>
                <IconButton
                  aria-label={msg({ id: 'page.chatbox.file.remove' })}
                  onClick={() => setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))}
                  size="small"
                  sx={{ marginLeft: 1 }}
                >
                  ✕
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {isDropZoneVisible ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80px',
              border: `2px dashed ${isDragOver ? '#1976d2' : '#9e9e9e'}`,
              borderRadius: '8px',
              backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <AttachFileIcon sx={{ fontSize: 40, color: isDragOver ? '#1976d2' : '#9e9e9e' }} />
            <MDTypography variant="body2" color={isDragOver ? 'primary' : 'textSecondary'}>
              {msg({ id: 'page.chatbox.dropzone.text' })}
            </MDTypography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {fileCountText && <FormHelperText sx={{ mb: 0.5 }}>{fileCountText}</FormHelperText>}
            {invalidFilesMessage && (
              <FormHelperText sx={{ mb: 0.5, color: 'error.main' }}>
                {invalidFilesMessage}
              </FormHelperText>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <MDInput
                inputRef={messageInputRef}
                fullWidth
                placeholder={msg({ id: 'page.chatbox.input.placeholder' })}
                value={message}
                onChange={handleMessageChange}
                error={!!error}
                sx={{
                  '.MuiOutlinedInput-root': { borderRadius: 5 },
                  '.MuiInputBase-input': { paddingLeft: 0 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {maxFiles > 0 && (
                        <label htmlFor="icon-button-file">
                          <IconButton
                            component="span"
                            aria-label={msg({
                              id:
                                (restrictions?.maxFilesCount ?? 0) > 1
                                  ? 'page.chatbox.files.attach'
                                  : 'page.chatbox.file.attach'
                            })}
                          >
                            <Tooltip
                              title={msg({
                                id:
                                  (restrictions?.maxFilesCount ?? 0) > 1
                                    ? 'page.chatbox.files.attach'
                                    : 'page.chatbox.file.attach'
                              })}
                            >
                              <AttachFileIcon />
                            </Tooltip>
                          </IconButton>
                          <input
                            accept="*"
                            style={{ display: 'none' }}
                            id="icon-button-file"
                            type="file"
                            multiple={(restrictions?.maxFilesCount ?? 0) > 1}
                            onChange={handleFileChange}
                          />
                        </label>
                      )}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {messageLoading ? (
                        <IconButton
                          aria-label={msg({ id: 'page.chatbox.cancel.request' })}
                          onClick={onCancel}
                        >
                          <Tooltip title={msg({ id: 'page.chatbox.cancel.request' })}>
                            <StopCircle />
                          </Tooltip>
                        </IconButton>
                      ) : (
                        <IconButton
                          type="submit"
                          aria-label={msg({ id: 'page.chatbox.send.message' })}
                          disabled={!message.trim() && files.length === 0}
                        >
                          <Tooltip title={msg({ id: 'page.chatbox.send.message' })}>
                            <SendRounded />
                          </Tooltip>
                        </IconButton>
                      )}
                    </InputAdornment>
                  )
                }}
              />

              {!isEmbedded && (
                <IconButton
                  component="span"
                  aria-label={msg({ id: 'page.chatbox.file.attach' })}
                  sx={{ ml: 1 }}
                  onClick={handleRequestDetailsToggle(!requestDetailsDrawerOpen)}
                >
                  <Tooltip title={msg({ id: 'page.chatbot.requestDetails.btn.tooltip' })}>
                    <TuneRoundedIcon />
                  </Tooltip>
                </IconButton>
              )}
            </Box>
          </Box>
        )}

        {error && <FormHelperText error>{error}</FormHelperText>}
      </form>
      <Drawer
        variant="temporary"
        anchor="right"
        open={requestDetailsDrawerOpen}
        onClose={toggleDrawer(false)}
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: { width: 700, p: 2 }
        }}
      >
        <ChatBotRequestDetails request={request} onSubmit={handleRequestDetailsFormSubmit} />
      </Drawer>
    </>
  );

  function clearInputs() {
    setMessage('');
    setFiles([]);
    setInvalidFilesMessage('');
  }
}

ChatBoxInputArea.propTypes = {
  setMessages: PropTypes.func.isRequired,
  selectedAssistant: PropTypes.shape({
    code: PropTypes.string.isRequired,
    restrictions: PropTypes.shape({
      maxFilesCount: PropTypes.number,
      allowedExtensions: PropTypes.array
    })
  }),
  onMessageSend: PropTypes.func.isRequired,
  messageLoading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEmbedded: PropTypes.bool,
  dragInProgress: PropTypes.bool
};
